export interface StaticDataSearchPagesEntry {
  de: string
  en?: string
  fr?: string
  it?: string
}

// These enums have to be hardcoded here because we are importing this file in nuxt.config.ts.
enum DomainId {
  /** EYZ */
  eyz = 'eyz',
  /** Hoteljobs */
  hoteljobs_zuerich_ch = 'hoteljobs_zuerich_ch',
  /** Meeting */
  meeting_zuerich_com = 'meeting_zuerich_com',
  /** Rapperswil */
  rapperswil_zuerichsee_ch = 'rapperswil_zuerichsee_ch',
  /** ZT */
  zt_zuerich_com = 'zt_zuerich_com',
  /** Zürich */
  zuerich_com = 'zuerich_com',
  /** Zuerich Experience */
  zuerich_experience_com = 'zuerich_experience_com',
}

enum PlaceClassification {
  bars_lounges = 'bars_lounges',
  cafe = 'cafe',
  culture = 'culture',
  food_drinks = 'food_drinks',
  nature = 'nature',
  nightlife = 'nightlife',
  recreation = 'recreation',
  restaurant = 'restaurant',
  shopping = 'shopping',
  sightseeing = 'sightseeing',
  sport = 'sport',
  venue = 'venue',
  wellness = 'wellness',
}

export enum StaticEntityType {
  /** Zurich Card shop */
  CardShop = 'CARD_SHOP',
  /** Block: Detail page corona info */
  DetailPageCorona = 'DETAIL_PAGE_CORONA',
  /** Node: Error page 404 not found */
  ErrorPageNotFound = 'ERROR_PAGE_NOT_FOUND',
  /** Node: Error page 500 server error */
  ErrorPageServerError = 'ERROR_PAGE_SERVER_ERROR',
  /** Block: Footer content EYZ */
  FooterContentEyz = 'FOOTER_CONTENT_EYZ',
  /** Block: Footer content Hoteljobs */
  FooterContentHoteljobs = 'FOOTER_CONTENT_HOTELJOBS',
  /** Block: Footer content meeting */
  FooterContentMeeting = 'FOOTER_CONTENT_MEETING',
  /** Block: Footer content Rapperswil */
  FooterContentRapperswil = 'FOOTER_CONTENT_RAPPERSWIL',
  /** Block: Footer content Zuerich */
  FooterContentZuerich = 'FOOTER_CONTENT_ZUERICH',
  /** Block: Kinderregion Teaser */
  KinderregionTeaser = 'KINDERREGION_TEASER',
  /** Neighborhood Guide */
  NeighborhoodGuide = 'NEIGHBORHOOD_GUIDE',
  /** Filter page: Accomodation */
  SearchAccommodation = 'SEARCH_ACCOMMODATION',
  /** Filter page: Accomodation (EYZ) */
  SearchAccommodationEyz = 'SEARCH_ACCOMMODATION_EYZ',
  /** Filter page: Accomodation (Hoteljobs) */
  SearchAccommodationHoteljobs = 'SEARCH_ACCOMMODATION_HOTELJOBS',
  /** Filter page: Accomodation (Rapperswil) */
  SearchAccommodationRapperswil = 'SEARCH_ACCOMMODATION_RAPPERSWIL',
  /** Anura media library */
  SearchAnura = 'SEARCH_ANURA',
  /** Filter page: Incentives */
  SearchIncentives = 'SEARCH_INCENTIVES',
  /** Filter page: Tours */
  SearchTours = 'SEARCH_TOURS',
  /** Filter page: Tours (Rapperswil) */
  SearchToursRapperswil = 'SEARCH_TOURS_RAPPERSWIL',
  /** Filter page: Venues */
  SearchVenues = 'SEARCH_VENUES',
  /** Filter page: What to do */
  SearchWhatToDo = 'SEARCH_WHAT_TO_DO',
  /** Filter page: What to do (Rapperswil) */
  SearchWhatToDoRapperswil = 'SEARCH_WHAT_TO_DO_RAPPERSWIL',
  /** Block: Tourist Information Menu */
  TouristinformationMenu = 'TOURISTINFORMATION_MENU',
}

export const CATEGORY_COLORS: Record<string, string> = {
  [PlaceClassification.sightseeing]: 'lime',
  [PlaceClassification.food_drinks]: 'burgundy',
  [PlaceClassification.nightlife]: 'violett',
  [PlaceClassification.shopping]: 'pink',
  [PlaceClassification.culture]: 'orange',
  [PlaceClassification.recreation]: 'success',
}

export const CATEGORY_FACETS_MAP: Record<string, string[]> = {
  [PlaceClassification.sightseeing]: ['attractions', 'nature'],
  [PlaceClassification.food_drinks]: ['restaurant', 'cuisine', 'meal'],
  [PlaceClassification.nightlife]: [
    'atmosphere',
    'club_disco',
    'bar_lounge',
    'casino',
  ],
  [PlaceClassification.shopping]: ['shopping', 'products'],
  [PlaceClassification.culture]: ['culture', 'museum'],
  [PlaceClassification.recreation]: ['nature', 'sport', 'wellness'],
}

export const HIDDEN_FACETS: string[] = [
  ...Object.keys(CATEGORY_FACETS_MAP)
    .map((v) => CATEGORY_FACETS_MAP[v])
    .flat(),
  'filter_category',
]

const staticDataSearchPages = {
  tours: {
    de: '/de/fuehrungen-ausfluege-in-zuerich',
    en: '/en/tours-in-zurich',
    fr: '/fr/visites-guidees-a-zurich',
    it: '/it/visite-guidate-a-zurigo',
  },
  venues: {
    de: '/de/business/zuerich-vielfalt-eventlokalitaeten',
    en: '/en/business/zurich-a-diverse-range-of-event-venues',
  },
  incentives: {
    de: '/de/business/die-perfekten-rahmenprogramme-fuer-ihre-events-in-zuerich',
    en: '/en/business/the-perfect-social-programs-for-your-events-in-zurich',
  },
  whatToDo: {
    de: '/de/zuerich-erleben',
    en: '/en/discover-zurich',
    fr: '/fr/decouvrez-zurich',
    it: '/it/vivere-zurigo',
  },
  accomodation: {
    de: '/de/uebernachten-zuerich-hotel-bed-and-breakfast-hostel',
    en: '/en/hotels-bbs-apartments-and-hostels-your-accommodation-in-zurich',
    fr: '/fr/tous-les-hebergements-a-zurich',
    it: '/it/dove-dormire-a-zurigo',
  },
  toursRapperswil: {
    de: '/de/fuehrungen-gruppenangebote-in-der-region-zuerichsee',
  },
  whatToDoRapperswil: {
    de: '/de/erlebnisse-in-der-region-zuerichsee',
  },
  accomodationRapperswil: {
    de: '/de/hotel-bed-and-breakfast-zeltplatz-uebernachten-zuerichsee',
    en: '/en/hotels-bbs-and-camping-site-find-your-accomodation-around-lake-zurich',
  },
  accomodationEyz: {
    de: '/de/besuchen/unterkunft/alle-angebote',
  },
  accomodationHoteljobs: {
    de: '/de/hotel-uebersicht',
    en: '/en/hotel-overview',
  },
}

export enum SearchSiteType {
  WhatToDo = 'what-to-do',
  Venues = 'venues',
  Hotel = 'hotel',
  Tours = 'tours',
  Incentives = 'incentives',
}

export class SearchSite {
  id: string
  searchType: SearchSiteType
  urls: StaticDataSearchPagesEntry
  hasMap: boolean
  domain: DomainId
  node: StaticEntityType
  hasLocationFilter: boolean
  hasCurrentlyOpenFilter: boolean
  hasSpecialOffersFilter: boolean

  constructor(
    id: string,
    searchType: SearchSiteType,
    urls: StaticDataSearchPagesEntry,
    hasMap: boolean,
    domain: DomainId,
    node: StaticEntityType,
    hasLocationFilter = false,
    hasCurrentlyOpenFilter = false,
    hasSpecialOffersFilter = false,
  ) {
    this.id = 'search-' + id
    this.searchType = searchType
    this.urls = urls
    this.hasMap = hasMap
    this.domain = domain
    this.node = node
    this.hasLocationFilter = hasLocationFilter
    this.hasCurrentlyOpenFilter = hasCurrentlyOpenFilter
    this.hasSpecialOffersFilter = hasSpecialOffersFilter
  }
}

export const SEARCH_SITES = [
  new SearchSite(
    'what-to-do-zurich',
    SearchSiteType.WhatToDo,
    staticDataSearchPages.whatToDo,
    true,
    DomainId.zuerich_com,
    StaticEntityType.SearchWhatToDo,
    true,
    true,
  ),

  new SearchSite(
    'what-to-do-rapperswil',
    SearchSiteType.WhatToDo,
    staticDataSearchPages.whatToDoRapperswil,
    true,
    DomainId.rapperswil_zuerichsee_ch,
    StaticEntityType.SearchWhatToDoRapperswil,
    true,
    true,
  ),

  new SearchSite(
    'tours-zurich',
    SearchSiteType.Tours,
    staticDataSearchPages.tours,
    false,
    DomainId.zuerich_com,
    StaticEntityType.SearchTours,
  ),

  new SearchSite(
    'tours-rapperswil',
    SearchSiteType.Tours,
    staticDataSearchPages.toursRapperswil,
    false,
    DomainId.rapperswil_zuerichsee_ch,
    StaticEntityType.SearchToursRapperswil,
  ),

  new SearchSite(
    'hotel-zurich',
    SearchSiteType.Hotel,
    staticDataSearchPages.accomodation,
    true,
    DomainId.zuerich_com,
    StaticEntityType.SearchAccommodation,
    true,
  ),

  new SearchSite(
    'hotel-rapperswil',
    SearchSiteType.Hotel,
    staticDataSearchPages.accomodationRapperswil,
    true,
    DomainId.rapperswil_zuerichsee_ch,
    StaticEntityType.SearchAccommodationRapperswil,
    true,
  ),

  new SearchSite(
    'hotel-eyz',
    SearchSiteType.Hotel,
    staticDataSearchPages.accomodationEyz,
    true,
    DomainId.eyz,
    StaticEntityType.SearchAccommodationEyz,
    true,
  ),

  new SearchSite(
    'hotel-hoteljobs',
    SearchSiteType.Hotel,
    staticDataSearchPages.accomodationHoteljobs,
    true,
    DomainId.hoteljobs_zuerich_ch,
    StaticEntityType.SearchAccommodationHoteljobs,
    true,
  ),

  new SearchSite(
    'venues',
    SearchSiteType.Venues,
    staticDataSearchPages.venues,
    true,
    DomainId.zuerich_com,
    StaticEntityType.SearchVenues,
    true,
    false,
    true,
  ),

  new SearchSite(
    'incentives',
    SearchSiteType.Incentives,
    staticDataSearchPages.incentives,
    false,
    DomainId.zuerich_com,
    StaticEntityType.SearchIncentives,
    false,
    false,
  ),
]
